// tk-header — mobile drawer (<dialog>), desktop dropdowns (<details>), sticky modes and
// --header-height / --header-group-height on <body> (Horizon reads these for offsets).
class TkHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-drawer]');
    this.opener = this.querySelector('[data-drawer-open]');
    this.dropdowns = [...this.querySelectorAll('[data-dropdown]')];
    this.sticky = this.dataset.sticky || 'none';
    this.section = this.closest('.shopify-section') || this;

    this.listeners = [
      [this.opener, 'click', () => this.openDrawer()],
      [this.querySelector('[data-drawer-close]'), 'click', () => this.closeDrawer()],
      [this.drawer, 'click', (event) => event.target === this.drawer && this.closeDrawer()],
      [this.drawer, 'close', () => this.onDrawerClosed()],
      [document, 'click', (event) => !this.contains(event.target) && this.closeDropdowns()],
      [this, 'keydown', (event) => this.onKeydown(event)],
      [this, 'focusout', (event) => this.onFocusOut(event)],
      [window, 'scroll', () => this.onScroll(), { passive: true }],
    ];
    for (const details of this.dropdowns) {
      this.listeners.push([details, 'toggle', () => details.open && this.closeDropdowns(details)]);
      this.listeners.push([details, 'pointerenter', () => this.hoverOpen(details, true)]);
      this.listeners.push([details, 'pointerleave', () => this.hoverOpen(details, false)]);
      // With a mouse, hover already opened it: a click on the summary must not close it again.
      this.listeners.push([
        details.querySelector('summary'),
        'click',
        (event) => {
          if (details.open && TkHeader.canHover()) event.preventDefault();
        },
      ]);
    }
    for (const [target, type, handler, options] of this.listeners) {
      target?.addEventListener(type, handler, options);
    }

    this.section.classList.add('tk-header-section');
    this.section.dataset.sticky = this.sticky;
    this.lastY = window.scrollY;
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(this);
    this.measure();
  }

  disconnectedCallback() {
    for (const [target, type, handler, options] of this.listeners ?? []) {
      target?.removeEventListener(type, handler, options);
    }
    this.resizeObserver?.disconnect();
    document.body.style.removeProperty('overflow');
  }

  measure() {
    const height = Math.round(this.getBoundingClientRect().height);
    document.body.style.setProperty('--header-height', `${height}px`);
    const group = document.querySelector('#header-group');
    if (!group) return;
    let total = 0;
    for (const child of group.children) {
      if (child instanceof HTMLElement) total += child.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${total}px`);
  }

  openDrawer() {
    if (!this.drawer || this.drawer.open) return;
    this.drawer.showModal();
    this.opener?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.drawer?.close();
  }

  onDrawerClosed() {
    this.opener?.setAttribute('aria-expanded', 'false');
    document.body.style.removeProperty('overflow');
    this.opener?.focus();
  }

  closeDropdowns(except) {
    for (const details of this.dropdowns) {
      if (details !== except) details.open = false;
    }
  }

  static canHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  hoverOpen(details, open) {
    if (!TkHeader.canHover()) return;
    details.open = open;
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const open = this.dropdowns.find((details) => details.open);
    if (!open) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  }

  onFocusOut(event) {
    for (const details of this.dropdowns) {
      if (details.open && !details.contains(event.relatedTarget)) details.open = false;
    }
  }

  onScroll() {
    if (this.sticky !== 'scroll-up') return;
    const y = window.scrollY;
    const hidden = y > this.lastY && y > this.offsetHeight;
    this.section.toggleAttribute('data-hidden', hidden);
    if (hidden) this.closeDropdowns();
    this.lastY = y;
  }
}

if (!customElements.get('tk-header')) customElements.define('tk-header', TkHeader);
